import { supabase } from '../supabase.ts';
import { parseTimeRange, toTimeRange } from './locations.ts';

/**
 * Commerce policy: what the store charges beyond the goods, how much a
 * shopper may buy, when an order can be collected, and which channels sell.
 *
 * All four tables are read by code that already exists -- `resolveOrderFees`,
 * `check_purchase_limits`, the checkout slot picker and the menu -- and none
 * of them had a screen, so every one of these settings was seeded once and
 * then frozen.
 */

export type FeeKind = 'delivery' | 'service' | 'payment_surcharge' | 'other';
export type FulfillmentType = 'pickup' | 'delivery' | 'curbside' | 'walk_in' | 'kiosk';

export const FEE_KINDS: FeeKind[] = ['service', 'delivery', 'payment_surcharge', 'other'];
export const FULFILLMENT_TYPES: FulfillmentType[] = [
  'pickup',
  'delivery',
  'curbside',
  'walk_in',
  'kiosk',
];

export interface FeeRecord {
  id: string;
  name: string;
  kind: FeeKind;
  amountCents: number | null;
  rateBps: number | null;
  taxable: boolean;
  fulfillmentTypes: FulfillmentType[] | null;
  active: boolean;
}

export async function listFees(accountId: string): Promise<FeeRecord[]> {
  const { data, error } = await supabase
    .from('fee_definitions')
    .select('id, name, kind, amount_cents, rate_bps, taxable, fulfillment_types, active')
    .eq('account_id', accountId)
    .order('name', { ascending: true });

  if (error) throw error;

  return (data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    kind: row.kind as FeeKind,
    amountCents: row.amount_cents,
    rateBps: row.rate_bps,
    taxable: row.taxable,
    fulfillmentTypes: (row.fulfillment_types as FulfillmentType[] | null) ?? null,
    active: row.active,
  }));
}

export type FeeInput = Omit<FeeRecord, 'id'>;

export async function saveFee(accountId: string, input: FeeInput & { id?: string }): Promise<void> {
  const row = {
    name: input.name,
    kind: input.kind,
    amount_cents: input.amountCents,
    rate_bps: input.rateBps,
    taxable: input.taxable,
    fulfillment_types: input.fulfillmentTypes,
    active: input.active,
  };

  const { error } = input.id
    ? await supabase
        .from('fee_definitions')
        .update(row)
        .eq('account_id', accountId)
        .eq('id', input.id)
    : await supabase.from('fee_definitions').insert({ account_id: accountId, ...row });

  if (error) throw error;
}

export async function deleteFee(accountId: string, id: string): Promise<void> {
  const { error } = await supabase
    .from('fee_definitions')
    .delete()
    .eq('account_id', accountId)
    .eq('id', id);

  if (error) throw error;
}

/* -------------------------------------------------------------------------- */
/* Purchase limits                                                             */
/* -------------------------------------------------------------------------- */

export const REGULATORY_CATEGORIES = [
  'flower',
  'preroll',
  'concentrate',
  'edible',
  'tincture',
  'topical',
  'vape',
  'seed',
  'clone',
  'thc_total',
] as const;

export const LIMIT_UNITS = ['grams', 'milligrams_thc', 'ounces'] as const;

export interface LimitRecord {
  id: string;
  jurisdiction: string;
  regulatoryCategory: (typeof REGULATORY_CATEGORIES)[number];
  unit: (typeof LIMIT_UNITS)[number];
  limitValue: number;
  windowHours: number;
  customerType: 'recreational' | 'medical' | 'state_medical' | null;
  active: boolean;
}

export async function listLimits(accountId: string): Promise<LimitRecord[]> {
  const { data, error } = await supabase
    .from('purchase_limit_rules')
    .select(
      'id, jurisdiction, regulatory_category, unit, limit_value, window_hours, customer_type, active',
    )
    .eq('account_id', accountId)
    .order('regulatory_category', { ascending: true });

  if (error) throw error;

  return (data ?? []).map((row) => ({
    id: row.id,
    jurisdiction: row.jurisdiction,
    regulatoryCategory: row.regulatory_category as LimitRecord['regulatoryCategory'],
    unit: row.unit as LimitRecord['unit'],
    limitValue: Number(row.limit_value),
    windowHours: row.window_hours,
    customerType: row.customer_type as LimitRecord['customerType'],
    active: row.active,
  }));
}

export type LimitInput = Omit<LimitRecord, 'id'>;

export async function saveLimit(
  accountId: string,
  input: LimitInput & { id?: string },
): Promise<void> {
  const row = {
    jurisdiction: input.jurisdiction,
    regulatory_category: input.regulatoryCategory,
    unit: input.unit,
    limit_value: input.limitValue,
    window_hours: input.windowHours,
    customer_type: input.customerType,
    active: input.active,
  };

  const { error } = input.id
    ? await supabase
        .from('purchase_limit_rules')
        .update(row)
        .eq('account_id', accountId)
        .eq('id', input.id)
    : await supabase.from('purchase_limit_rules').insert({ account_id: accountId, ...row });

  if (error) throw error;
}

export async function deleteLimit(accountId: string, id: string): Promise<void> {
  const { error } = await supabase
    .from('purchase_limit_rules')
    .delete()
    .eq('account_id', accountId)
    .eq('id', id);

  if (error) throw error;
}

/* -------------------------------------------------------------------------- */
/* Pickup slots                                                                */
/* -------------------------------------------------------------------------- */

export interface PickupSlotRow {
  id: number;
  locationId: string;
  dayOfWeek: number;
  fulfillmentType: 'pickup' | 'curbside';
  startsAt: string;
  endsAt: string;
  capacity: number | null;
  active: boolean;
}

export async function listPickupSlots(
  accountId: string,
  locationId: string,
): Promise<PickupSlotRow[]> {
  const { data, error } = await supabase
    .from('pickup_slot_templates')
    .select('id, location_id, day_of_week, fulfillment_type, slot, capacity, active')
    .eq('account_id', accountId)
    .eq('location_id', locationId)
    .order('day_of_week', { ascending: true });

  if (error) throw error;

  return (
    (data ?? []) as unknown as Array<{
      id: number;
      location_id: string;
      day_of_week: number;
      fulfillment_type: 'pickup' | 'curbside';
      slot: string;
      capacity: number | null;
      active: boolean;
    }>
  ).flatMap((row) => {
    const parsed = parseTimeRange(String(row.slot));

    return parsed
      ? [
          {
            id: row.id,
            locationId: row.location_id,
            dayOfWeek: row.day_of_week,
            fulfillmentType: row.fulfillment_type,
            startsAt: parsed.opensAt,
            endsAt: parsed.closesAt,
            capacity: row.capacity,
            active: row.active,
          },
        ]
      : [];
  });
}

export async function addPickupSlot(
  accountId: string,
  input: {
    locationId: string;
    dayOfWeek: number;
    fulfillmentType: 'pickup' | 'curbside';
    startsAt: string;
    endsAt: string;
    capacity: number | null;
  },
): Promise<void> {
  const { error } = await supabase.from('pickup_slot_templates').insert({
    account_id: accountId,
    location_id: input.locationId,
    day_of_week: input.dayOfWeek,
    fulfillment_type: input.fulfillmentType,
    slot: toTimeRange(input.startsAt, input.endsAt),
    capacity: input.capacity,
  });

  if (error) throw error;
}

export async function deletePickupSlot(accountId: string, id: number): Promise<void> {
  const { error } = await supabase
    .from('pickup_slot_templates')
    .delete()
    .eq('account_id', accountId)
    .eq('id', id);

  if (error) throw error;
}

/** Same shape as the delivery windows: one day authored, then repeated. */
export async function copyPickupSlotsToWeek(
  accountId: string,
  locationId: string,
  source: PickupSlotRow[],
): Promise<void> {
  for (const day of [0, 1, 2, 3, 4, 5, 6]) {
    for (const slot of source) {
      if (slot.dayOfWeek === day) continue;

      // One clash must not abandon the rest of the week.
      await addPickupSlot(accountId, {
        locationId,
        dayOfWeek: day,
        fulfillmentType: slot.fulfillmentType,
        startsAt: slot.startsAt,
        endsAt: slot.endsAt,
        capacity: slot.capacity,
      }).catch(() => undefined);
    }
  }
}

/* -------------------------------------------------------------------------- */
/* Channels                                                                    */
/* -------------------------------------------------------------------------- */

export type ChannelType = 'web' | 'pos' | 'kiosk' | 'marketplace';

export const CHANNEL_TYPES: ChannelType[] = ['web', 'pos', 'kiosk', 'marketplace'];

export interface ChannelRecord {
  id: string;
  name: string;
  slug: string;
  type: ChannelType;
  active: boolean;
}

export async function listChannels(accountId: string): Promise<ChannelRecord[]> {
  const { data, error } = await supabase
    .from('sales_channels')
    .select('id, name, slug, type, active')
    .eq('account_id', accountId)
    .order('name', { ascending: true });

  if (error) throw error;

  return (data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    slug: row.slug,
    type: row.type as ChannelType,
    active: row.active,
  }));
}

export async function saveChannel(
  accountId: string,
  input: { id?: string; name: string; slug: string; type: ChannelType; active: boolean },
): Promise<void> {
  const row = { name: input.name, slug: input.slug, type: input.type, active: input.active };

  const { error } = input.id
    ? await supabase
        .from('sales_channels')
        .update(row)
        .eq('account_id', accountId)
        .eq('id', input.id)
    : await supabase.from('sales_channels').insert({ account_id: accountId, ...row });

  if (error) throw error;
}

export async function deleteChannel(accountId: string, id: string): Promise<void> {
  const { error } = await supabase
    .from('sales_channels')
    .delete()
    .eq('account_id', accountId)
    .eq('id', id);

  if (error) throw error;
}

export interface ChannelMenuRecord {
  id: string;
  channelId: string;
  locationId: string;
  published: boolean;
  showOutOfStock: boolean;
  excludedProducts: number;
}

/** Which catalog each channel shows, per location. */
export async function listChannelMenus(accountId: string): Promise<ChannelMenuRecord[]> {
  const { data, error } = await supabase
    .from('channel_menus')
    .select(
      'id, channel_id, location_id, published, show_out_of_stock, channel_menu_exclusions(product_id)',
    )
    .eq('account_id', accountId);

  if (error) throw error;

  return (
    (data ?? []) as unknown as Array<{
      id: string;
      channel_id: string;
      location_id: string;
      published: boolean;
      show_out_of_stock: boolean;
      channel_menu_exclusions: Array<{ product_id: string }> | null;
    }>
  ).map((row) => ({
    id: row.id,
    channelId: row.channel_id,
    locationId: row.location_id,
    published: row.published,
    showOutOfStock: row.show_out_of_stock,
    excludedProducts: row.channel_menu_exclusions?.length ?? 0,
  }));
}

export async function saveChannelMenu(
  accountId: string,
  input: {
    id?: string;
    channelId: string;
    locationId: string;
    published: boolean;
    showOutOfStock: boolean;
  },
): Promise<void> {
  const row = {
    channel_id: input.channelId,
    location_id: input.locationId,
    published: input.published,
    published_at: input.published ? new Date().toISOString() : null,
    show_out_of_stock: input.showOutOfStock,
  };

  const { error } = input.id
    ? await supabase
        .from('channel_menus')
        .update(row)
        .eq('account_id', accountId)
        .eq('id', input.id)
    : await supabase.from('channel_menus').insert({ account_id: accountId, ...row });

  if (error) throw error;
}

export async function deleteChannelMenu(accountId: string, id: string): Promise<void> {
  const { error } = await supabase
    .from('channel_menus')
    .delete()
    .eq('account_id', accountId)
    .eq('id', id);

  if (error) throw error;
}
